















import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import gtmService from '@/project/tracking/gtm/GTM.service';
import { Route } from 'vue-router';
import megaMenuService from '@/project/shared/megaMenu.service';
import dictionaryService, { NamedLabelArgument } from '@/core/translation/dictionary.service';
import domService from '@/core/dom/dom.service';
import { upperFirst } from 'lodash';
import CategoryNavigation from '@/project/search/navigation/CategoryNavigationV1.vue';
import { PageData } from '@/types/contentServerContract';
import CategoryTileBlock from '@/project/app/blocks/categories/CategoryTileBlock.vue';
import botService from '@/project/site/bot.service';
import { CategorySeoInformationViewModel, Feature, MenuItem, SearchViewModel } from '@/types/apiServerContract';
import Api from '@/project/http/Api.service';
import urlHelper from '@/core/urlHelper.service';
import { UrlQueryKey } from '@/core/urlQueryKey';
import serverContextService from '@/core/serverContext.service';
const SearchProductControl = () => serverContextService.isFeatureEnabled(Feature.ProductListingPageV2)
    ? import('@/project/search/SearchProductControl.vue')
    : import('@/project/search/SearchProductControlV1.vue');
@Component({
    components: { CategoryTileBlock, SearchProductControl, CategoryNavigation }
})
export default class CategoryPage extends Vue {
    @Prop(String)
    categoryId!: string;

    previousCategoryId: string = '';
    isFirstRequest: boolean = true;
    delayLoaderTimer: number = 0;

    searchResult: SearchViewModel | null = null;
    seoInformation: CategorySeoInformationViewModel | null = null;
    seoCategoryName: null | string = null;
    seoBreadcrumb: MenuItem[] = [];

    get pageDataUrl() {
        if (serverContextService.settings?.departmentPage) {
            const pageUrl = `${serverContextService.settings?.departmentPage}?categoryId=${this.categoryId}`;
            return pageUrl;
        }
    }

    async handlePageDataCallback(pageData?: PageData) {
        const activeCategory = this.categoryId ? await megaMenuService.getCategoryById(this.categoryId) : null;

        const page = urlHelper.getPage();
        if (activeCategory?.url && page > 1 && !activeCategory?.url.includes(`${UrlQueryKey.pageKey}=`)) {
            const location = urlHelper.getLocationForPage(page) as any;
            const query = location.query || {};
            const paramString = Object.entries(query).map(([key, val]) => `${key}=${val}`).join('&');
            activeCategory.url = `${activeCategory.url}${(activeCategory.url.includes('?') ? '&' : '?')}${paramString}`;
        }

        domService.setCanonicalUrl(window.servercontext.culture + activeCategory?.url ?? '', true);
        domService.setOpenGraphUrl(window.servercontext.culture + activeCategory?.url ?? '', true);
        const expectedCategoryUrl = activeCategory?.url ? `${serverContextService.siteInfo.url}${activeCategory.url}` : null;
        botService.setPrerenderRedirectUrlsIfLocationIsWrong(expectedCategoryUrl);
    }

    async handleSearchResultCallback(searchResult: SearchViewModel) {
        const delay = (botService.uaIsPrerender() || botService.uaIsBot()) ? 0 : this.isFirstRequest ? 1500 : 750;
        this.isFirstRequest = false;
        this.searchResult = searchResult;
        this.delayLoaderTimer = setTimeout(() => {
            this.loadMetadataForPage();
        }, delay);
    }

    async loadMetadataForPage() {
        if (this.categoryId !== this.previousCategoryId) {
            Api.category.getCategorySeoInformation(this.categoryId).then(result => {
                this.seoInformation = result;
                this.updatePageTitleAndDescription();
            });
            megaMenuService.getBreadCrumbForCategory(this.categoryId).then(result => {
                this.seoBreadcrumb = result || [];
                this.updatePageTitleAndDescription();
            });
            this.previousCategoryId = this.categoryId;
        }
    }

    updatePageTitleAndDescription() {
        const categoryValues = {};
        this.seoBreadcrumb.map((cat, catIndex) => {
            categoryValues[`categoryLevel${catIndex + 1}`] = cat.name;
        });
        const activeCategory = this.categoryId && this.seoBreadcrumb.length > 0 ? this.seoBreadcrumb[this.seoBreadcrumb.length - 1] : null;
        this.seoCategoryName = upperFirst(activeCategory?.name ?? 'Unknown');

        const totalHits = dictionaryService.seofyNumbers(this.searchResult?.totalHits);
        const totalResults = totalHits !== undefined ? totalHits.toString(10) : undefined;
        const labelValues: NamedLabelArgument = {
            brand: this.searchResult?.brandName || undefined,
            totalResults: totalResults,
            results: totalResults,
            category: this.seoCategoryName,
            ...categoryValues
        };

        const titleLabelKey = 'Category.SEO.Title';
        const title = dictionaryService.get(titleLabelKey, [this.seoCategoryName]);
        const titleFromPage = this.seoInformation?.seoTitle;
        const titleWithParams = dictionaryService.getWithNamedValues(titleFromPage || titleLabelKey, labelValues, true, !!titleFromPage);
        domService.setTitle(titleWithParams || title);

        const descriptionLabelKey = 'Category.SEO.Description';
        const description = dictionaryService.get(descriptionLabelKey, [this.seoCategoryName]);
        const descriptionFromPage = this.seoInformation?.seoDescription;
        const descriptionWithParams = dictionaryService.getWithNamedValues(descriptionFromPage || descriptionLabelKey, labelValues, true, !!descriptionFromPage);
        domService.setDescription(descriptionWithParams || description);
    }

    @Watch('$route', {
        immediate: true
    })
    onRouteChange(to: Route, from?: Route) {
        const isSearching = urlHelper.isSearching(to) || urlHelper.isSearching(from);
        if (isSearching) return;

        if (this.$route.name) {
            gtmService.trackGenericPageView(this.$route.name);
        }
    }

    destroyed() {
        if (this.delayLoaderTimer) {
            clearTimeout(this.delayLoaderTimer);
        }
    }
}
